import React, { useState, useEffect, useCallback } from "react";
import { Col, Container, Row, Card, CardBody } from "reactstrap";
import { Button } from "react-bootstrap";
import { useHistory, Link } from "react-router-dom";
import { HiOutlineTrash } from "react-icons/hi";
import { FaImage, FaStar, FaFire } from "react-icons/fa";
import axios, { get } from "axios";
import { ToastContainer, toast } from "react-toastify";
import BootstrapTable from "react-bootstrap-table-next";
import { Pagination } from "@mui/material";
import { FiEdit2 } from "react-icons/fi";
import config from "../../../../config";
import Breadcrumbs from "../../../Helper/Breadcrumbs";
import Loader from "../../../Helper/Loader";
import CustomSearch from "../../../Helper/CustomSearch";

const PAGE_SIZE = 20;

/** Parse backend date formats → "09 Jun 2026" */
const formatDate = (dtStr) => {
  if (!dtStr) return "—";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  // Backend custom format: "DD/MM/YYYY HH:MM:SS..."
  const custom = dtStr.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (custom) {
    const [, dd, mm, yyyy] = custom;
    return `${dd} ${months[parseInt(mm, 10) - 1]} ${yyyy}`;
  }
  // ISO fallback
  const d = new Date(dtStr);
  if (!isNaN(d.getTime()))
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  return "—";
};

const BlogData = () => {
  const history = useHistory();
  const tokenKey = {
    headers: { Authorization: "Token " + sessionStorage.getItem("tokenKey") },
  };
  const toastAttr = {
    theme: "colored",
    position: "top-center",
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  };
  const breadcrumbData = [
    { title: "Seobook", href: "/seobook" },
    { title: "Blog Data", href: "#" },
  ];

  const [tableData, settableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState({ current: 1, total: 1, count: 0 });

  const fetchData = useCallback((search = "", pageNum = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: pageNum, page_size: PAGE_SIZE });
    if (search) params.set("search", search);

    get(`${config.service}/masters/blogpost/?${params}`, tokenKey)
      .then((res) => {
        const results = res.data?.results ?? [];
        const count = res.data?.count ?? results.length;
        settableData(results);
        setPage({ current: pageNum, total: Math.ceil(count / PAGE_SIZE) || 1, count });
      })
      .catch((err) => {
        if (err.response?.status === 401) window.location.href = "/";
        else toast.error("Failed to load blogs", toastAttr);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (term) => { setSearchTerm(term); fetchData(term, 1); };
  const handlePageChange = (_, value) => fetchData(searchTerm, value);

  const deleteRow = (row) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    axios
      .delete(`${config.service}/masters/blogpost/${row.id}`, tokenKey)
      .then(() => fetchData(searchTerm, page.current))
      .catch((err) => {
        if (err.response?.status === 401) window.location.href = "/";
        else toast.error("Failed to delete blog", toastAttr);
      });
  };

  const renderForm = (row) =>
    history.push({
      pathname: "/seobook/blogDataForm",
      state: row ? { id: row.id, editMode: true } : {},
    });

  const cols = [
    {
      dataField: "title",
      text: "Blog",
      formatter: (cell, row) => (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", minWidth: 280 }}>
          {/* Thumbnail */}
          {row.image ? (
            <img
              src={row.image}
              alt={cell}
              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
              style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 6, flexShrink: 0, border: "1px solid #eee" }}
            />
          ) : null}
          <div
            style={{
              width: 54, height: 54, borderRadius: 6, background: "#f5f5f5", flexShrink: 0,
              alignItems: "center", justifyContent: "center", border: "1px solid #eee",
              display: row.image ? "none" : "flex",
            }}
          >
            <FaImage style={{ color: "#ccc", fontSize: 20 }} />
          </div>
          {/* Text */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, lineHeight: "19px", color: "#222", marginBottom: 3 }}>
              {cell || "—"}
            </div>
            <div
              style={{
                fontSize: 12, color: "#888", lineHeight: "17px",
                overflow: "hidden", display: "-webkit-box",
                WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              }}
            >
              {row.short_content || "—"}
            </div>
          </div>
        </div>
      ),
    },
    {
      dataField: "author",
      text: "Author",
      headerStyle: () => ({ width: 110, whiteSpace: "nowrap" }),
      formatter: (cell) => (
        <span style={{ fontSize: 13, color: "#555" }}>{cell || "—"}</span>
      ),
    },
    {
      dataField: "is_featured",
      text: "Tags",
      headerStyle: () => ({ width: 70, whiteSpace: "nowrap" }),
      formatter: (cell, row) => (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <FaStar
            title="Featured"
            style={{ fontSize: 14, color: cell ? "#f5a623" : "#ddd" }}
          />
          <FaFire
            title="Popular"
            style={{ fontSize: 14, color: row.is_popular ? "#e44d26" : "#ddd" }}
          />
        </div>
      ),
    },
    {
      dataField: "status",
      text: "Status",
      headerStyle: () => ({ width: 90, whiteSpace: "nowrap" }),
      formatter: (cell) =>
        cell ? (
          <span className="badge badge-success">Active</span>
        ) : (
          <span className="badge badge-secondary">Inactive</span>
        ),
    },
    {
      dataField: "updated_at",
      text: "Updated",
      headerStyle: () => ({ width: 100, whiteSpace: "nowrap" }),
      formatter: (cell) => (
        <span style={{ fontSize: 12, color: "#777", whiteSpace: "nowrap" }}>
          {formatDate(cell)}
        </span>
      ),
    },
    {
      dataField: "id",
      text: "Actions",
      headerStyle: () => ({ width: 80, whiteSpace: "nowrap" }),
      events: { onClick: (e) => e.stopPropagation() },
      formatter: (cell, row) => (
        <div className="tableActions">
          <Link to={{ pathname: "/seobook/blogDataForm/", state: { id: row.id, editMode: true } }}>
            <FiEdit2 title="Edit" />
          </Link>
          <HiOutlineTrash title="Delete" onClick={() => deleteRow(row)} />
        </div>
      ),
    },
  ];

  const rowEvents = {
    onClick: (e, row) => renderForm(row),
  };

  return (
    <Container className="vendor">
      <Row>
        <Col md={12} className="contBody">
          <Card>
            <CardBody>
              <div className="card__title pageNav">
                <div className="bold-text">
                  <Breadcrumbs data={breadcrumbData} />
                </div>
              </div>

              <Row className="formBtnsDiv mar-b-10" style={{ alignItems: "center" }}>
                <Col md={6} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Button variant="primary" onClick={() => renderForm()}>
                    + Create Blog
                  </Button>
                  {page.count > 0 && (
                    <span style={{ fontSize: 13, color: "#888" }}>
                      {page.count} blog{page.count !== 1 ? "s" : ""}
                    </span>
                  )}
                </Col>
                <Col md={6} style={{ display: "flex", justifyContent: "flex-end" }}>
                  <CustomSearch callbackFn={handleSearch} />
                </Col>
              </Row>

              <Row className="vendorList">
                <Col md={12}>
                  <Loader loading={loading}>
                    <BootstrapTable
                      bootstrap4
                      wrapperClasses="table-responsive pad-20"
                      data={tableData}
                      columns={cols}
                      keyField="id"
                      hover
                      noDataIndication={
                        <div style={{ textAlign: "center", padding: "32px 0", color: "#aaa" }}>
                          {searchTerm
                            ? `No blogs found for "${searchTerm}"`
                            : "No blogs yet. Click '+ Create Blog' to add one."}
                        </div>
                      }
                      rowEvents={rowEvents}
                      rowStyle={{ cursor: "pointer" }}
                    />
                  </Loader>
                </Col>
              </Row>

              {page.total > 1 && (
                <Row className="mar-t-30 mar-b-20 justify-content-center">
                  <Pagination
                    count={page.total}
                    page={page.current}
                    onChange={handlePageChange}
                    shape="rounded"
                    sx={{
                      "& .Mui-selected": { backgroundColor: "#EC628A !important", color: "#fff !important" },
                      "& .MuiPaginationItem-root:hover": { backgroundColor: "rgba(236,98,138,0.1)" },
                    }}
                  />
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Container>
  );
};

export default BlogData;
